/**
 * v3.4.2 — <LearnRuleDialog>
 * =============================================================================
 * Extracted from CSVImport.js to kill the cursor-lag Diya reported while
 * typing in the "Pattern" input.
 *
 * Root cause of the lag: the old inline dialog bound the pattern input
 * directly to `learnDialog` state, which lives on the 3,780-line
 * CSVImport component. Each keystroke fired setLearnDialog(...), which
 * re-rendered the entire preview table + drop zone + dedup banner +
 * every dependent memo — 100ms+ per key on slower devices.
 *
 * Fix: this component owns its own local state for pattern / category /
 * applyAll. The parent only supplies INITIAL values (via `trigger`) and
 * an `onSave({pattern, category, applyAll})` callback. Typing is now a
 * local state update — the parent tree doesn't re-render.
 *
 * Contract with parent:
 *   trigger = null  → dialog closed.
 *   trigger = {
 *     index,          // row index in previewRows
 *     pattern,        // initial suggested pattern
 *     category,       // initial suggested category
 *     description,    // original row description (shown as context)
 *     type,           // transaction type ('expense' | 'income' | 'investment' | 'transfer' | 'refund')
 *     applyAll,       // default toggle state (usually true)
 *   }
 *   onClose()                       → user hit Cancel / clicked outside.
 *   onSave({pattern, category, applyAll})  → user hit Remember.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { CategorySelect } from '@/components/CategorySelect';
import { Sparkles } from 'lucide-react';

export default function LearnRuleDialog({ trigger, onClose, onSave }) {
  const [pattern, setPattern] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('expense');
  const [applyAll, setApplyAll] = useState(true);
  const [saving, setSaving] = useState(false);

  // Seed local state from trigger every time the dialog opens.
  useEffect(() => {
    if (trigger) {
      setPattern(trigger.pattern || '');
      setCategory(trigger.category || '');
      setType(trigger.type || 'expense');
      setApplyAll(trigger.applyAll !== false);
      setSaving(false);
    }
  }, [trigger]);

  const handleSave = async () => {
    if (!onSave || saving) return;
    setSaving(true);
    try {
      await onSave({ pattern, category, applyAll });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!trigger} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent className="rounded-2xl sm:max-w-lg max-w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Remember this for next time?</DialogTitle>
          <DialogDescription>
            We&rsquo;ll automatically categorize any future transaction whose description contains the pattern below.
          </DialogDescription>
        </DialogHeader>
        {trigger && (
          <div className="space-y-3 min-w-0">
            <div className="text-xs text-muted-foreground">From description:</div>
            <div
              className="rounded-lg bg-muted/60 px-3 py-2 text-xs break-words"
              title={trigger.description}
            >
              {trigger.description}
            </div>
            <div className="space-y-1.5">
              <div className="text-xs text-muted-foreground">Pattern (edit if needed):</div>
              <Input
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                className="rounded-xl w-full"
                data-testid="learn-pattern-input"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <div className="text-xs text-muted-foreground">Category:</div>
              <CategorySelect
                type={type}
                value={category}
                onChange={setCategory}
                triggerClassName="rounded-xl w-full"
                contentClassName="max-h-[50vh]"
                testId="learn-category-select"
              />
            </div>
            <div className="flex items-start justify-between gap-3 rounded-xl border border-border/70 px-3 py-2.5">
              <div className="text-xs min-w-0 pr-2 leading-snug">Also apply to other matching rows in this batch</div>
              <Switch
                checked={applyAll}
                onCheckedChange={setApplyAll}
                data-testid="learn-apply-all-switch"
              />
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 flex-col-reverse sm:flex-row sm:justify-end pt-2">
          <Button variant="ghost" onClick={onClose} className="rounded-xl w-full sm:w-auto" disabled={saving}>Not now</Button>
          <Button
            onClick={handleSave}
            className="rounded-xl w-full sm:w-auto"
            data-testid="learn-save-button"
            disabled={saving || !pattern || !category}
          >
            <Sparkles className="h-4 w-4 mr-1 text-[hsl(var(--gold))]" /> Remember
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
